import { Controller } from "@hotwired/stimulus"

const STORAGE_PREFIX = "deepco:banner:"

export default class extends Controller {
  static identifier = "banner-notification"
  static targets = ["banner", "indicator", "indicatorButton"]
  static values = {
    notificationKey: String
  }

  connect() {
    this.initializeVisibility()
    this.syncState()
  }

  notificationKeyValueChanged() {
    this.syncState()
  }

  bannerTargetConnected(element) {
    const key = element.dataset.notificationKey
    if (key) {
      this.notificationKeyValue = key
    }
    this.syncState()
  }

  bannerTargetDisconnected() {
    this.syncState()
  }

  pin(event) {
    event.preventDefault()
    this.setPinned(true)
    this.syncState()
  }

  toggleFromIndicator(event) {
    event.preventDefault()
    if (!this.hasBannerTarget) return

    const pinned = this.isPinned()
    this.setPinned(!pinned)
    this.syncState()
  }

  syncState() {
    if (!this.hasBannerTarget) {
      this.hideIndicator()
      this.clearPinnedState()
      return
    }

    this.showIndicator()

    if (this.isPinned()) {
      this.hideBannerElement()
      this.setIndicatorExpanded(false)
      this.setIndicatorPinned(true)
    } else {
      this.showBannerElement()
      this.setIndicatorExpanded(true)
      this.setIndicatorPinned(false)
    }
  }

  isPinned() {
    const key = this.storageKey
    if (!key) return false

    try {
      return window.localStorage.getItem(key) === "pinned"
    } catch (error) {
      return false
    }
  }

  setPinned(pinned) {
    const key = this.storageKey
    if (!key) return

    try {
      if (pinned) {
        window.localStorage.setItem(key, "pinned")
      } else {
        window.localStorage.removeItem(key)
      }
    } catch (error) {
      // Ignore storage access issues (e.g., Safari private mode)
    }
  }

  clearPinnedState() {
    const key = this.storageKey
    if (!key) return
    this.setPinned(false)
  }

  get storageKey() {
    if (!this.hasNotificationKeyValue || !this.notificationKeyValue) return null
    return `${STORAGE_PREFIX}${this.notificationKeyValue}`
  }

  showIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.remove("hidden")
    }
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.add("hidden")
    }

    if (this.hasIndicatorButtonTarget) {
      this.indicatorButtonTarget.setAttribute("aria-expanded", "false")
      this.indicatorButtonTarget.classList.remove("btn-active")
    }
  }

  showBannerElement() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("hidden")
      this.bannerTarget.removeAttribute("aria-hidden")
    }
  }

  hideBannerElement() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden")
      this.bannerTarget.setAttribute("aria-hidden", "true")
    }
  }

  setIndicatorExpanded(expanded) {
    if (!this.hasIndicatorButtonTarget) return
    this.indicatorButtonTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  setIndicatorPinned(pinned) {
    if (!this.hasIndicatorButtonTarget) return
    this.indicatorButtonTarget.classList.toggle("btn-active", pinned)
  }

  initializeVisibility() {
    if (!this.hasBannerTarget) return
    if (this.isPinned()) {
      this.hideBannerElement()
    } else {
      this.showBannerElement()
    }
  }
}
